import classNames from 'classnames';
import { useEffect } from 'react';
import { REDUX_APP_SET_WEATHER_WARNING_BANNER } from 'redux-state/app/reducer.js';
import { useAppDispatch, useAppSelector } from 'redux-state/configure-store.js';
import { REDUX_HEADER_WARNINGS_BAR_UPDATE } from 'redux-state/header-warning-bar/reducer.js';
import { ILocationWarningState, WarningLoadingState } from 'redux-state/weather-warnings/types.js';
import { PageLocationSummary } from 'types/markerTypes/location.page.js';
import { WarningsHeaderLink } from './header-link.js';
import { WarningsPanelHeader } from './panel-header.js';
import { WarningBarAddAsLink } from './utils/redirect-to-url.js';
import { WarningContent } from './warning-panel-body.js';
import { LoadedType, WeatherWarningPanelData } from './warning-panel-data.js';
import { trackNavigateEvent } from 'utilities/posthog-tracking';

export interface IWeatherWarningPanelProps {
  location?: PageLocationSummary;
  /** to test component */
  overrideWarningsData?: ILocationWarningState;
}
/**
 * Weather warning panel shown at top of page
 * Will always display if warningsFailed
 */
export const WeatherWarningPanel = ({ location, overrideWarningsData }: IWeatherWarningPanelProps) => {
  const appDispatch = useAppDispatch();
  const { warningState, warningMeta } = WeatherWarningPanelData({ location, overrideWarningsData });
  const bannerOpen = useAppSelector((s) => s.APP.weatherWarningBannerOpen);

  // Toggle open close of warning panel
  const toggleWarningDetails = () => {
    const newBannerState = !bannerOpen;
    appDispatch(REDUX_APP_SET_WEATHER_WARNING_BANNER({ isOpen: newBannerState }));
  };

  useEffect(() => {
    appDispatch(REDUX_HEADER_WARNINGS_BAR_UPDATE({ colourClass: warningState?.colorClass }));
  });

  if (location) {
    const hasDropdown =
      warningState?.type === WarningLoadingState.loaded &&
      warningState?.hasWarnings &&
      warningState?.loadedType === LoadedType.REGIONAL;

    const warningsClasses = classNames('WeatherWarning-bar-warnings u-flexAlignItemsCenter u-flex u-bL-sm', {
      'u-mL-xs u-bL-0 u-pL-0': !hasDropdown,
    });

    const dropDownStyle = {
      flex: 1,
      flexGrow: 0,
      flexBasis: '16px',
    };

    return (
      <div
        className={classNames(
          `WeatherWarning ${warningState?.colorClass}`,
          { 'is-open': bannerOpen },
          { 'is-clickable': hasDropdown },
        )}
      >
        <div className="u-flex u-flexCol u-flexGrow1">
          <WarningBarAddAsLink link={!hasDropdown} url={warningMeta?.warningPageUrl ?? ''}
            onClick={hasDropdown ? undefined : () => trackNavigateEvent("severe_weather_banner", "view_all_link")}>
            <>
              <div
                onClick={hasDropdown ? toggleWarningDetails : undefined}
                className="WeatherWarning-header WeatherWarning-bar u-flexAlignItemsCenter u-flex u-flexGrow1"
              >
                <div className="warnings-header-content u-flex-1">
                  <ul className={warningsClasses}>
                    <WarningsPanelHeader warningState={warningState} warningMeta={warningMeta} />
                  </ul>
                </div>
                <div className="warnings-header-content" style={hasDropdown ? dropDownStyle : {}}>
                  <WarningsHeaderLink hasDropdown={hasDropdown} warningState={warningState} />
                </div>
              </div>
              {hasDropdown && <WarningContent warnings={warningMeta?.warnings} />}
            </>
          </WarningBarAddAsLink>
        </div>
      </div>
    );
  }
  return null;
};
